// Fill/stroke resolution for symbol SVGs. The assets style shapes three ways —
// presentation attributes (fill="none"), `<style>` class blocks (.st0 {...}),
// and (rarely) inline style="" — and inherit through <g> wrappers. The export
// only needs paint *presence*: an element draws iff its effective stroke or
// fill is not "none" (SVG's initial paint is fill:black, stroke:none).

export const INITIAL_PAINT = { fill: 'black', stroke: 'none' };

const PAINT_PROPS = ['fill', 'stroke'];

/** Parse the class rules out of `<style>` text. Only single-class selectors are
 * supported — the full asset set uses nothing else. Rules keep stylesheet order
 * (equal specificity → the later rule wins). */
export function parseStylesheet(cssText) {
  const rules = [];
  const text = String(cssText ?? '').replace(/\/\*[\s\S]*?\*\//g, '');
  for (const match of text.matchAll(/([^{}]+)\{([^{}]*)\}/g)) {
    const selectors = match[1].split(',').map((s) => s.trim());
    const classNames = selectors.filter((s) => /^\.[\w-]+$/.test(s)).map((s) => s.slice(1));
    if (classNames.length === 0) continue;
    rules.push({ classNames, declarations: parseDeclarations(match[2]) });
  }
  return rules;
}

export function parseDeclarations(declText) {
  const out = {};
  for (const decl of String(declText ?? '').split(';')) {
    const colon = decl.indexOf(':');
    if (colon < 0) continue;
    const prop = decl.slice(0, colon).trim().toLowerCase();
    const value = decl.slice(colon + 1).trim();
    if (prop && value) out[prop] = value;
  }
  return out;
}

/**
 * The element's effective paint, given the inherited paint of its parent.
 * Cascade per property: style="" attr > class rules (later wins) > presentation
 * attribute > inherited value.
 */
export function resolvePaint(el, rules, inherited) {
  const own = {};

  for (const prop of PAINT_PROPS) {
    const attr = el.getAttribute(prop);
    if (attr != null && attr.trim() !== '') own[prop] = attr.trim();
  }

  const classAttr = el.getAttribute('class');
  if (classAttr) {
    const classes = new Set(classAttr.split(/\s+/).filter(Boolean));
    for (const rule of rules) {
      if (!rule.classNames.some((c) => classes.has(c))) continue;
      for (const prop of PAINT_PROPS) {
        if (rule.declarations[prop] != null) own[prop] = rule.declarations[prop];
      }
    }
  }

  const styleAttr = el.getAttribute('style');
  if (styleAttr) {
    const decls = parseDeclarations(styleAttr);
    for (const prop of PAINT_PROPS) {
      if (decls[prop] != null) own[prop] = decls[prop];
    }
  }

  const resolveValue = (value, inheritedValue) => {
    if (value == null || value.toLowerCase() === 'inherit') return inheritedValue;
    return value;
  };

  return {
    fill: resolveValue(own.fill, inherited.fill),
    stroke: resolveValue(own.stroke, inherited.stroke),
  };
}

/** Whether a shape with this effective paint produces visible marks on canvas. */
export const isVisiblePaint = (paint) =>
  paint.stroke.toLowerCase() !== 'none' || paint.fill.toLowerCase() !== 'none';
